import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { photoUrl } from "../lib/api";
import { Button } from "./ui/button";
import { Images, MapPin, ArrowRight, User } from "lucide-react";

function WallImage({ item, onClick }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`wall-photo-${item.path.split("/").pop()}`}
      className="group relative mb-3 block w-full overflow-hidden rounded-xl border border-[#D8E0E8] bg-[#E3EAF0]"
    >
      {!loaded && <span className="block h-52 w-full animate-pulse bg-[#E3EAF0]" />}
      <img
        src={photoUrl(item.path)}
        alt={item.caption || item.member.full_name}
        loading="lazy"
        onLoad={() => setLoaded(true)}
        className={`w-full object-cover transition-all duration-500 ${loaded ? "opacity-100" : "absolute opacity-0"} group-hover:scale-[1.03]`}
      />
      <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-2 bg-gradient-to-t from-black/70 to-transparent p-2.5 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
        <span className="truncate text-left text-xs font-medium text-white">{item.member.full_name}</span>
        {item.year && <span className="shrink-0 rounded bg-white/25 px-1.5 py-0.5 text-[10px] text-white">{item.year}</span>}
      </span>
    </button>
  );
}

export function PhotoWall({ members, onSelect }) {
  const items = useMemo(() => {
    const out = [];
    members.forEach((m) => {
      const meta = Object.fromEntries((m.photo_meta || []).map((x) => [x.path, x]));
      (m.gallery || []).forEach((path) => out.push({ path, member: m, caption: meta[path]?.caption || "", year: meta[path]?.year || "" }));
    });
    return out;
  }, [members]);
  const [active, setActive] = useState(null);

  if (!items.length) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <div className="max-w-sm">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8EEF3] text-[#082D4A]"><Images size={26} /></div>
          <h2 className="font-serif text-2xl font-semibold text-[#082D4A]">The family photo wall</h2>
          <p className="mt-2 text-sm text-[#64748B]">Add photos to relatives (open anyone and use "Add photo", or the "Photos" bulk import) and they'll gather here in one beautiful collage.</p>
        </div>
      </div>
    );
  }

  const cur = active != null ? items[active] : null;

  return (
    <div className="h-full overflow-y-auto bg-[#F4F6F8] px-5 py-6 sm:px-8" data-testid="photo-wall">
      <div className="mx-auto mb-5 max-w-5xl">
        <h2 className="font-serif text-2xl font-semibold text-[#082D4A]">Family photo wall</h2>
        <p className="text-sm text-[#64748B]">{items.length} memories from across the family</p>
      </div>
      <div className="mx-auto max-w-5xl columns-2 gap-3 sm:columns-3 lg:columns-4 [&>button]:break-inside-avoid">
        {items.map((item, i) => (
          <WallImage key={item.path} item={item} onClick={() => setActive(i)} />
        ))}
      </div>

      <Dialog open={active != null} onOpenChange={(o) => { if (!o) setActive(null); }}>
        <DialogContent className="max-w-2xl border-none bg-[#141310] p-0" data-testid="wall-lightbox">
          <DialogTitle className="sr-only">{cur?.member.full_name} photo</DialogTitle>
          <DialogDescription className="sr-only">A photo from the family photo wall.</DialogDescription>
          {cur && (
            <div>
              <img src={photoUrl(cur.path)} alt="" className="max-h-[68vh] w-full rounded-t-lg bg-black object-contain" />
              <div className="flex items-center justify-between gap-3 p-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-white">
                    <span className="h-8 w-8 shrink-0 overflow-hidden rounded-full border border-white/20 bg-white/10">
                      {photoUrl(cur.member.photo_path) ? <img src={photoUrl(cur.member.photo_path)} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-white/50"><User size={15} /></span>}
                    </span>
                    <span className="truncate font-serif text-lg">{cur.member.full_name}</span>
                    {cur.year && <span className="shrink-0 rounded bg-white/15 px-1.5 py-0.5 text-xs text-white/90">{cur.year}</span>}
                  </div>
                  {cur.caption && <p className="mt-1 text-sm text-white/70">{cur.caption}</p>}
                  {cur.member.origin_place && <p className="mt-0.5 flex items-center gap-1 text-xs text-white/50"><MapPin size={11} /> {cur.member.origin_place}</p>}
                </div>
                <Button onClick={() => { setActive(null); onSelect?.(cur.member.id); }} data-testid="wall-view-person" className="shrink-0 bg-[#D4A017] text-white hover:bg-[#B0850F]">
                  View {cur.member.full_name.split(" ")[0]} <ArrowRight size={15} className="ml-1.5" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
